import React, { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import Select from 'react-select';

export default function SalaryHeadListView() {
    const { http, user } = AuthUser();
    
    const [loading, setLoading] = useState(true);
    const [salaryHeads, setSalaryHeads] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingHead, setEditingHead] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        action: 'addition',
        remarks: ''
    });

    const actionOptions = [
        { value: 'addition', label: 'Addition (+)' },
        { value: 'deduction', label: 'Deduction (-)' }
    ];

    useEffect(() => {
        fetchSalaryHeads();
    }, []);

    const fetchSalaryHeads = async () => {
        try {
            const response = await http.get(`/salary_master_heads?login_user=${user.id}`);
            if (response.data.success) {
                setSalaryHeads(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching salary heads:', error);
            toast.error('Error loading salary heads');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name) {
            toast.error('Please enter head name');
            return;
        }

        try {
            const requestData = {
                ...formData,
                login_user: user.id
            };

            let response;
            if (editingHead) {
                response = await http.put(`/salary_master_head/${editingHead.id}`, requestData);
            } else {
                response = await http.post('/salary_master_head', requestData);
            }

            if (response.data.success) {
                toast.success(editingHead ? 'Updated successfully!' : 'Created successfully!');
                setShowModal(false);
                setEditingHead(null);
                setFormData({ name: '', action: 'addition', remarks: '' });
                fetchSalaryHeads();
            }
        } catch (error) {
            console.error('Error saving:', error);
            toast.error('Error saving record');
        }
    };

    const handleEdit = (head) => {
        setEditingHead(head);
        setFormData({
            name: head.name || '',
            action: head.action || 'addition',
            remarks: head.remarks || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this head?')) return;

        try {
            const response = await http.delete(`/salary_master_head/${id}`);
            if (response.data.success) {
                toast.success('Deleted successfully!');
                fetchSalaryHeads();
            }
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error('Error deleting record');
        }
    };

    const openAddModal = () => {
        setEditingHead(null);
        setFormData({ name: '', action: 'addition', remarks: '' });
        setShowModal(true);
    };

    const additionHeads = salaryHeads.filter(h => h.action === 'addition');
    const deductionHeads = salaryHeads.filter(h => h.action === 'deduction');

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-2 px-3">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="mb-0"><i className="fa fa-list me-2"></i>Salary Heads Master</h5>
                        <small className="text-muted">Define salary heads (amounts will be entered when generating invoice)</small>
                    </div>
                    <button className="btn btn-primary" onClick={openAddModal}>
                        <i className="fa fa-plus me-1"></i>Add New Head
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        {/* Additions */}
                        <div className="col-md-6 mb-3">
                            <div className="card h-100 border-success">
                                <div className="card-header py-2" style={{ background: '#d4edda' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0 text-success">
                                            <i className="fa fa-plus-circle me-2"></i>Addition Heads
                                        </h6>
                                        <span className="badge bg-success">{additionHeads.length} items</span>
                                    </div>
                                </div>
                                <div className="card-body p-0">
                                    {additionHeads.length === 0 ? (
                                        <div className="text-center py-4 text-muted">
                                            <i className="fa fa-inbox fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                                            <p className="mb-0">No addition heads defined</p>
                                        </div>
                                    ) : (
                                        <div className="list-group list-group-flush">
                                            {additionHeads.map((head, idx) => (
                                                <div key={head.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <span className="badge bg-success me-2">{idx + 1}</span>
                                                        <strong>{head.name}</strong>
                                                        {head.remarks && (
                                                            <small className="text-muted d-block ms-4">{head.remarks}</small>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-1"
                                                            onClick={() => handleEdit(head)}
                                                            title="Edit"
                                                        >
                                                            <i className="fa fa-edit"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDelete(head.id)}
                                                            title="Delete"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Deductions */}
                        <div className="col-md-6 mb-3">
                            <div className="card h-100 border-danger">
                                <div className="card-header py-2" style={{ background: '#f8d7da' }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0 text-danger">
                                            <i className="fa fa-minus-circle me-2"></i>Deduction Heads
                                        </h6>
                                        <span className="badge bg-danger">{deductionHeads.length} items</span>
                                    </div>
                                </div>
                                <div className="card-body p-0">
                                    {deductionHeads.length === 0 ? (
                                        <div className="text-center py-4 text-muted">
                                            <i className="fa fa-inbox fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                                            <p className="mb-0">No deduction heads defined</p>
                                        </div>
                                    ) : (
                                        <div className="list-group list-group-flush">
                                            {deductionHeads.map((head, idx) => (
                                                <div key={head.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <span className="badge bg-danger me-2">{idx + 1}</span>
                                                        <strong>{head.name}</strong>
                                                        {head.remarks && (
                                                            <small className="text-muted d-block ms-4">{head.remarks}</small>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-1"
                                                            onClick={() => handleEdit(head)}
                                                            title="Edit"
                                                        >
                                                            <i className="fa fa-edit"></i>
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDelete(head.id)}
                                                            title="Delete"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Info Card */}
                <div className="card bg-light">
                    <div className="card-body py-2">
                        <div className="d-flex align-items-center">
                            <i className="fa fa-info-circle text-info me-2"></i>
                            <small className="text-muted">
                                These are head templates. When generating salary invoice, you'll enter the specific amount for each head per employee.
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#f8f9fa' }}>
                                <h6 className="modal-title">
                                    <i className={`fa ${editingHead ? 'fa-edit' : 'fa-plus-circle'} me-2`}></i>
                                    {editingHead ? 'Edit' : 'Add'} Salary Head
                                </h6>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Head Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="e.g., Bonus, Travel Allowance, Late Deduction"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Type <span className="text-danger">*</span></label>
                                        <Select
                                            options={actionOptions}
                                            value={actionOptions.find(opt => opt.value === formData.action)}
                                            onChange={(opt) => setFormData(prev => ({ ...prev, action: opt ? opt.value : 'addition' }))}
                                            placeholder="Select type..."
                                            styles={{
                                                control: (base) => ({ ...base, borderColor: '#dee2e6' }),
                                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                                option: (base, { data }) => ({
                                                    ...base,
                                                    color: data.value === 'addition' ? '#198754' : '#dc3545'
                                                })
                                            }}
                                        />
                                        <small className="text-muted">
                                            {formData.action === 'addition' 
                                                ? 'This head will ADD to the salary' 
                                                : 'This head will DEDUCT from the salary'}
                                        </small>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Remarks <small className="text-muted">(Optional)</small></label>
                                        <textarea
                                            className="form-control"
                                            name="remarks"
                                            value={formData.remarks}
                                            onChange={handleInputChange}
                                            rows="2"
                                            placeholder="Description or notes about this head..."
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer py-2">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        <i className="fa fa-save me-1"></i>
                                        {editingHead ? 'Update' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
